import { useState } from 'react';
import { useMediaQuery } from '@mui/material';
import { Tabs, Tab, TextField, InputAdornment, IconButton, Chip } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import TuneIcon from '@mui/icons-material/Tune';
import NameCardPage from '@RegistriesPages/NameCardPage';
import OrganizationCardPage from '@RegistriesPages/OrganizationCardPage';
import LabelCardPage from '@RegistriesPages/LabelCardPage';
import LabelAnalytics from '@RegistriesPages/LabelAnalytics';
import { PiIdentificationCardLight as NameCardIcon } from "react-icons/pi";
import { PiBuildingsLight as OrganizationIcon } from "react-icons/pi";
import { ReactComponent as LabelIcon } from '@Assets/label.svg';
import ErrorHandler from '@Root/ErrorHandler';
import '@Partials/_ComStyles.scss';
import '@Partials/_SimpleStyles.scss';
import '@Partials/_OverrideStyles.scss';
import './RegistriesPage.scss';

export default function RegistriesPage(props) {
    const [showLabelAnalytics, setShowLabelAnalytics] = useState(false);
    const [labelData, setLabelData] = useState({});
    const [registryType, setRegistryType] = useState('');
    const [registryTabView, setRegistryTabView] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [displayList, setDisplayList] = useState([
        'nameCards',
        'organizationCards',
        'labelCards'
    ]);
    const allRegistries = [
        { id: 'nameCards', label: 'Name Cards', icon: NameCardIcon, index: 0 },
        { id: 'organizationCards', label: 'Organization Cards', icon: OrganizationIcon, index: 1 },
        { id: 'labelCards', label: 'Label Cards', icon: LabelIcon, index: 2 }
    ];
    new ErrorHandler();
    const isMobile = useMediaQuery('(max-width: 768px)');
    const className = 'registriesPage';

    const toggleRegistryTabView = (e, view) => {
        setRegistryTabView(view);
    };

    const handleSearch = (e) => {
        const searchString = e.target.value;
        setSearchQuery(searchString);
        
        if (searchString === '' || !searchString) {
            setDisplayList([
                'nameCards',
                'organizationCards',
                'labelCards'
            ]);
        } else {
            const filtered = allRegistries
                .filter(registry => 
                    registry.label.toLowerCase().includes(searchString.toLowerCase()) ||
                    registry.id.toLowerCase().includes(searchString.toLowerCase())
                )
                .map(registry => registry.id);
            setDisplayList(filtered);
        }
    };

    const clearSearch = () => {
        setSearchQuery('');
        setDisplayList([
            'nameCards',
            'organizationCards',
            'labelCards'
        ]);
    };

    const handleLabelAnalytics = (data) => {
        setShowLabelAnalytics(prev => !prev);
    };

    const handleLabelData = (data) => {
        setLabelData(data);
        const registryLabels = {
            0: 'nameCard',
            1: 'orgCard',
            2: 'labelCard'
        };
        setRegistryType(registryLabels[registryTabView] || '');
    };

    const getVisibleRegistries = () => {
        return allRegistries.filter(registry => displayList.includes(registry.id));
    };

    return (
        <div className={className}>
            <div className={`${className}__header`}>
                <div className={`${className}__header__content`}>
                    <div className={`${className}__header__title`}>
                        <TuneIcon className={`${className}__header__icon`} />
                        <h1>Registries & Configurations</h1>
                    </div>
                    <div className={`${className}__header__search`}>
                        <TextField
                            placeholder='Search registries...'
                            value={searchQuery}
                            onChange={handleSearch}
                            size='small'
                            fullWidth
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position='start'>
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                                endAdornment: searchQuery && (
                                    <InputAdornment position='end'>
                                        <IconButton size='small' onClick={clearSearch}>
                                            <ClearIcon fontSize='small' />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            className={`${className}__searchInput`}
                        />
                    </div>
                </div>
            </div>

            <div className={`${className}__body`}>
                {!isMobile && (
                    <div className={`${className}__sidebar`}>
                        <div className={`${className}__sidebar__header`}>
                            <span>Categories</span>
                            <Chip 
                                label={getVisibleRegistries().length} 
                                size='small' 
                                color='primary'
                                className={`${className}__sidebar__count`}
                            />
                        </div>
                        <Tabs
                            value={registryTabView}
                            onChange={toggleRegistryTabView}
                            orientation='vertical'
                            variant='scrollable'
                            className={`${className}__sidebar__tabs`}
                            TabIndicatorProps={{
                                className: `${className}__sidebar__indicator`
                            }}
                        >
                            {getVisibleRegistries().map((registry) => {
                                const IconComponent = registry.icon;
                                return (
                                    <Tab
                                        key={registry.id}
                                        value={registry.index}
                                        className={`${className}__sidebar__tab`}
                                        label={
                                            <div className={`${className}__sidebar__tab__content`}>
                                                <IconComponent className={`${className}__sidebar__tab__icon`} />
                                                <span className={`${className}__sidebar__tab__label`}>
                                                    {registry.label}
                                                </span>
                                            </div>
                                        }
                                    />
                                );
                            })}
                        </Tabs>
                    </div>
                )}

                {isMobile && (
                    <div className={`${className}__mobileTabs`}>
                        <Tabs
                            value={registryTabView}
                            onChange={toggleRegistryTabView}
                            variant='scrollable'
                            scrollButtons='auto'
                            className={`${className}__mobileTabs__container`}
                        >
                            {getVisibleRegistries().map((registry) => {
                                const IconComponent = registry.icon;
                                return (
                                    <Tab
                                        key={registry.id}
                                        value={registry.index}
                                        icon={<IconComponent />}
                                        label={registry.label}
                                        className={`${className}__mobileTabs__tab`}
                                    />
                                );
                            })}
                        </Tabs>
                    </div>
                )}

                <div className={`${className}__content`}>
                    <div className={`${className}__content__wrapper height-wfa`}>
                        {registryTabView === 0 && (
                            <NameCardPage
                                handleLabelAnalytics={handleLabelAnalytics}
                                handleLabelData={handleLabelData}
                            />
                        )}
                        {registryTabView === 1 && (
                            <OrganizationCardPage
                                handleLabelAnalytics={handleLabelAnalytics}
                                handleLabelData={handleLabelData}
                            />
                        )}
                        {registryTabView === 2 && (
                            <LabelCardPage
                                handleLabelAnalytics={handleLabelAnalytics}
                                handleLabelData={handleLabelData}
                            />
                        )}
                    </div>
                </div>
            </div>

            {showLabelAnalytics && (
                <LabelAnalytics
                    labelData={labelData}
                    registryType={registryType}
                    handleLabelAnalytics={handleLabelAnalytics}
                />
            )}
        </div>
    );
}
